import { MRT_RowSelectionState, MRT_Updater } from 'material-react-table';
import { useCallback, useMemo, useState } from 'react';

interface CurrentPageData {
    pk: number | string;
}

interface UseMultiPageSelectionReturn {
    allSelectedIds: Set<string>;
    allSelectedObjects: CurrentPageData[];
    rowSelectionForPage: MRT_RowSelectionState;
    handleSelectionChange: (updaterOrValue: MRT_Updater<MRT_RowSelectionState>) => void;
    seedIds: (ids: Array<number | string>, objects?: CurrentPageData[]) => void;
    reset: () => void;
}

export function useMultiPageSelection(currentPageData: CurrentPageData[]): UseMultiPageSelectionReturn {
    const [allSelectedIds, setAllSelectedIds] = useState<Set<string>>(new Set());
    const [allSelectedObjects, setAllSelectedObjects] = useState<CurrentPageData[]>([]);

    const rowSelectionForPage = useMemo<MRT_RowSelectionState>(() => {
        const result: MRT_RowSelectionState = {};
        currentPageData.forEach((item) => {
            const id = item.pk.toString();
            if (allSelectedIds.has(id)) result[id] = true;
        });
        return result;
    }, [currentPageData, allSelectedIds]);

    const handleSelectionChange = useCallback(
        (updaterOrValue: MRT_Updater<MRT_RowSelectionState>) => {
            const applyNext = (next: MRT_RowSelectionState) => {
                setAllSelectedIds((prevSet) => {
                    const newSet = new Set(prevSet);
                    Object.entries(next).forEach(([id, selected]) => {
                        if (selected) newSet.add(id);
                        else newSet.delete(id);
                    });
                    return newSet;
                });

                setAllSelectedObjects((prev) => {
                    let nextNew = [...prev];
                    Object.entries(next).forEach(([id, selected]) => {
                        const item = currentPageData.find((data) => String(data.pk) === id);
                        if (selected && item && !nextNew.some((obj) => String(obj.pk) === id)) {
                            nextNew.push(item);
                        } else if (!selected) {
                            nextNew = nextNew.filter((obj) => String(obj.pk) !== id);
                        }
                    });
                    return nextNew;
                });
            };

            if (typeof updaterOrValue === 'function') {
                applyNext(updaterOrValue(rowSelectionForPage));
            } else {
                applyNext(updaterOrValue);
            }
        },
        [currentPageData, rowSelectionForPage],
    );

    const seedIds = useCallback((ids: Array<number | string>, objects?: CurrentPageData[]) => {
        setAllSelectedIds((prev) => new Set([...prev, ...ids.map(String)]));
        if (objects) {
            setAllSelectedObjects((prev) => {
                const next = [...prev];
                objects.forEach((obj) => {
                    if (!next.some((o) => String(o.pk) === String(obj.pk))) {
                        next.push(obj);
                    }
                });
                return next;
            });
        }
    }, []);

    const reset = useCallback(() => {
        setAllSelectedIds(new Set());
        setAllSelectedObjects([]);
    }, []);

    return { allSelectedIds, allSelectedObjects, rowSelectionForPage, handleSelectionChange, seedIds, reset };
}
